import { useSavedQuote } from "@myaccount/_client/hooks/use-saved-quote";
import { useI18n } from "@wojo/localization";
import { TripTimingStatus, DepartureTimingStatus } from "generated/graphql";
import {
    useNbgcTripAndDeparture,
    useTripAndDeparture,
} from "./use-trips-and-departures";

export type TopLevelBreadcrumbsProps = {
    subPages?: {
        slug: string;
        text: string;
    }[];
};

export type MyToursBreadcrumbsProps = {
    isPastTour?: boolean;
    subPages?: {
        slug: string;
        text: string;
    }[];
};

export type BookingBreadcrumbsProps = {
    bookingId: string;
    subPages?: {
        slug: string;
        text: string;
    }[];
};

export type SavedQuoteBreadcrumbsProps = {
    quoteId: string;
    subPages?: {
        slug: string;
        text: string;
    }[];
};

export type NbgcBreadcrumbsProps = {
    gcDepartureId: string;
    subPages?: {
        slug: string;
        text: string;
    }[];
};

export const useMyToursBreadcrumbs = ({
    isPastTour,
    subPages,
}: MyToursBreadcrumbsProps) => {
    const globalI10n = useI18n("global").global.accountNavigation;

    return {
        items: [
            {
                link: isPastTour
                    ? globalI10n.pastTours.url.l()
                    : globalI10n.myTours.url.l(),
                text: isPastTour
                    ? globalI10n.pastTours.text.l()
                    : globalI10n.myTours.text.l(),
            },
            ...(subPages?.map((item) => ({
                link: item.slug,
                text: item.text,
            })) ?? []),
        ],
    };
};

export const useTopLevelBreadcrumbs = ({
    subPages,
}: TopLevelBreadcrumbsProps) => {
    return {
        items: [
            ...(subPages?.map((item) => ({
                link: item.slug,
                text: item.text,
            })) ?? []),
        ],
    };
};

export const useBookingBreadcrumbs = ({
    bookingId,
    subPages,
}: BookingBreadcrumbsProps) => {
    const accountI10N = useI18n("account").account;

    const { trip, tourName } = useTripAndDeparture(bookingId) ?? {};

    const isPastTour = trip?.timingStatus === TripTimingStatus.PostTour;
    const { items: topLevelItems } = useMyToursBreadcrumbs({ isPastTour });
    const items = [
        ...topLevelItems,
        {
            link: accountI10N.navigation.booking.bookingBaseUrl.l({
                bookingId: bookingId,
            }),
            text: tourName ?? accountI10N.bookingOverview.fallbackTourName.l(),
        },
        ...(subPages?.map((subPage) => ({
            link: accountI10N.navigation.booking.bookingLink.l({
                bookingId: bookingId,
                slug: subPage.slug,
            }),
            text: subPage.text,
        })) ?? []),
    ];

    return { items };
};

export const useSavedQuoteBreadcrumbs = ({
    quoteId,
}: SavedQuoteBreadcrumbsProps) => {
    const accountI10N = useI18n("account").account;

    const { quote } = useSavedQuote({
        id: quoteId,
        language: process.env.NEXT_PUBLIC_STORYBLOK_LANGUAGE ?? "",
    });

    const items = [
        {
            link: accountI10N.navigation.saved.slug.l(),
            text: accountI10N.navigation.saved.text.l(),
        },
        {
            link: "",
            text:
                quote.departure?.tourContent?.name ??
                accountI10N.savedQuotes.fallbackTourName.l(),
        },
    ];

    return { items };
};

export const useNbgcBreadcrumbs = ({
    gcDepartureId,
    subPages,
}: NbgcBreadcrumbsProps) => {
    const accountI10N = useI18n("account").account;
    const myGroupMembersL10n = accountI10N.navigation.booking.myGroupMembers;

    const { tourName, departure } =
        useNbgcTripAndDeparture(gcDepartureId) ?? {};

    const isPastTour =
        departure?.timingStatus === DepartureTimingStatus.PostTour;
    const { items: topLevelItems } = useMyToursBreadcrumbs({ isPastTour });

    if (!departure || !tourName || !myGroupMembersL10n) {
        return { items: [] };
    }

    const subPageItems =
        subPages?.map((subPage) => ({
            link: myGroupMembersL10n.departureLink.l({
                departureId: gcDepartureId,
                slug: subPage.slug,
            }),
            text: subPage.text,
        })) ?? [];

    const tourLevelLink =
        subPageItems.length === 1
            ? subPageItems[0].link
            : myGroupMembersL10n.baseDepartureLink.l({
                  departureId: gcDepartureId,
              });

    const items = [
        ...topLevelItems,
        {
            link: tourLevelLink,
            text: tourName,
        },
        ...subPageItems,
    ];

    return { items };
};
